import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Grid } from '@opendoor/atoms/grid';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import { getInitials } from 'components/landing-pages-v2/utils/getInitials';
import usePrevious from 'components/landing-pages-v2/utils/usePrevious';
import { useRouter } from 'next/router';

import { COSMOS_URL } from '../../../globals';
import MenuPanel from '../menu/MenuPanel';
import type { MegaNavItem, NavigationProps } from '../types';
import { AccountSection } from './AccountSection';
import { LogoSection } from './LogoSection';
import { MegaNav } from './MegaNav';
import { SkipLink } from './SkipLink';
import { useLargerThanSM } from './useLargerThanSM';

interface HeaderProps extends NavigationProps {
  megaNavData?: MegaNavItem[];
  closeMenuRef?: MutableRefObject<() => void>;
  /** Merged onto the sticky bar, e.g. to match a page ground other than primary. */
  className?: string;
}

const redirectToLogin = () => {
  document.location.href = `${COSMOS_URL}/login?redirectTo=${encodeURIComponent(
    window.location.pathname,
  )}`;
};

const Header = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaTitle,
  ctaLink,
  megaNavData: megaNavDataProp,
  closeMenuRef,
  className,
}: HeaderProps) => {
  const { pathname } = useRouter();
  const largerThanSM = useLargerThanSM();

  const { user: human, authentication } = useAuth();

  const isLoggedIn = authentication.state === 'authenticated';

  const initials = getInitials(human?.fullName, human?.firstName);
  const [isVisible, setIsVisible] = useState(false);
  const prevVisible = usePrevious(isVisible);
  const toggleMenuOpen = () => setIsVisible(!isVisible);

  // Hang on to burger button ref for focus management
  const ctaRef = useRef<HTMLButtonElement>(null);
  // When menu close animation is complete
  // and it was previously open.
  // This prevents a focus from the initial animation state
  // on page load.
  const exitComplete = () => {
    if (prevVisible === true) {
      const activeEl = document.activeElement;
      const isInputFocused =
        activeEl instanceof HTMLInputElement || activeEl instanceof HTMLTextAreaElement;
      if (!isInputFocused) {
        ctaRef.current?.focus();
      }
    }
  };

  const showLogin = !pathname.includes('/account');

  useEffect(() => {
    if (closeMenuRef) {
      closeMenuRef.current = () => setIsVisible(false);
    }
  }, [closeMenuRef]);

  return (
    <>
      {!largerThanSM && (
        <MenuPanel
          slug={slug}
          menuPanelNavLinks={menuPanelNavLinks}
          mainNavLinks={mainNavLinks}
          ctaTitle={ctaTitle}
          ctaLink={ctaLink}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          exitComplete={exitComplete}
          megaNavData={megaNavDataProp}
        />
      )}

      <SkipLink />
      <Box
        data-site-header
        className={cn(
          'sticky top-0 z-[2000] block h-18 bg-od-bg-primary antialiased [letter-spacing:-0.2016px] [text-rendering:optimizeLegibility]',
          className,
        )}
      >
        <Box className="flex h-18 items-center justify-center">
          <Grid className="h-18 w-full max-w-[1200px] grid-cols-[auto_1fr_auto] items-center px-5">
            <LogoSection />
            <MegaNav slug={slug} mainNavLinks={mainNavLinks} megaNavData={megaNavDataProp} />
            <Row className="row-start-1 row-end-2 [grid-column:-1/-2] items-center pl-2">
              <AccountSection
                isLoggedIn={isLoggedIn}
                isAuthenticating={authentication.state === 'authenticating'}
                showLogin={showLogin}
                initials={initials}
                redirectToLogin={redirectToLogin}
                onMobileMenuToggle={toggleMenuOpen}
                isMobileMenuVisible={isVisible}
                hamburgerRef={ctaRef}
              />
            </Row>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default Header;
